import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

export interface ConsumerUser {
  id: string
  email: string
  name: string
  display_name: string | null
  subscription_tier: string
  onboarding_completed: boolean
  is_active: boolean
  created_at: string | null
  last_login: string | null
}

interface ConsumerUserListResponse {
  users: ConsumerUser[]
  total: number
  limit: number
  offset: number
}

interface ConsumerDetail {
  id: string
  email: string
  name: string
  role: string
  is_active: boolean
  created_at: string | null
  last_login: string | null
  consumer_profile: {
    id: string
    display_name: string | null
    subscription_tier: string
    onboarding_completed: boolean
    onboarding_step: string | null
    created_at: string | null
  } | null
  session_stats?: {
    total_sessions: number
    total_utterances: number
    total_distillations: number
  }
}

interface ConsumerAnalytics {
  period_days: number
  total_consumers: number
  active_consumers: number
  new_registrations: number
  tier_distribution: Record<string, number>
  onboarding_completion_rate: number
}

interface MaturityDistribution {
  maturity_distribution: Record<string, number>
  total_profiles: number
}

interface ExtensionStats {
  period_days: number
  total_sessions: number
  total_utterances: number
  total_distillations: number
  total_insights: number
  active_users: number
}

interface UseConsumerUsersOptions {
  search?: string
  tier?: string
  is_active?: boolean
  limit?: number
  offset?: number
}

export function useConsumerUsers(options: UseConsumerUsersOptions = {}) {
  const { search, tier, is_active, limit = 50, offset = 0 } = options

  return useQuery<ConsumerUserListResponse>({
    queryKey: ['admin-consumers', { search, tier, is_active, limit, offset }],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      if (tier) params.append('tier', tier)
      if (is_active !== undefined) params.append('is_active', String(is_active))
      params.append('limit', String(limit))
      params.append('offset', String(offset))
      const { data } = await api.get(`/admin/consumer/users?${params}`)
      return data
    },
  })
}

export function useConsumerUser(id: string) {
  return useQuery<ConsumerDetail>({
    queryKey: ['admin-consumer', id],
    queryFn: async () => {
      const { data } = await api.get(`/admin/consumer/users/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export function useSuspendConsumer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.put(`/admin/consumer/users/${id}/suspend`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-consumers'] })
    },
  })
}

export function useActivateConsumer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.put(`/admin/consumer/users/${id}/activate`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-consumers'] })
    },
  })
}

export function useConsumerAnalytics(days: number = 30) {
  return useQuery<ConsumerAnalytics>({
    queryKey: ['admin-consumer-analytics', days],
    queryFn: async () => {
      const { data } = await api.get(`/admin/consumer/analytics?days=${days}`)
      return data
    },
  })
}

export function useConsumerMaturityDistribution() {
  return useQuery<MaturityDistribution>({
    queryKey: ['admin-consumer-maturity'],
    queryFn: async () => {
      const { data } = await api.get('/admin/consumer/analytics/maturity')
      return data
    },
  })
}

export function useConsumerExtensionStats(days: number = 30) {
  return useQuery<ExtensionStats>({
    queryKey: ['admin-consumer-extension', days],
    queryFn: async () => {
      const { data } = await api.get(`/admin/consumer/analytics/extension?days=${days}`)
      return data
    },
  })
}
